import React, { useRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { ResponsiveBox, HStack, Typography, Box} from "../primitives";
import CommonContainerBox from "../custom/containers/CommonContainerBox";
import ERPSBaseButton from "../custom/inputs/ERPSBaseButton";
import ERPSBaseDatePicker from "../custom/inputs/ERPSDatePicker";
import ERPSBaseRadioGroup from "../custom/inputs/ERPSBaseRadioGroup";
import ERPSBaseTextField from "../custom/inputs/ERPSBaseTextField";
import ErrorMessage from "../custom/containers/ErrorMessage";
import moment from "moment";
import {transactionTypes, Regex, RoleType, minStartDateAdjustments, minEndDateAdjustments, FI} from "../../constants/sharedConstants.js";
import { sanitizeQueryParams, maxCharOccurrence, checkFieldValue } from "../../utils/helper";
import {isShowBankCard, isShowSubscribingEntity} from "../../constants/TransactionSearchConstants.js";


const ERPSRecallRejectSearch = (props) => {
  const {
    pageType, // "RT", "RPC", "RPM", "RRH", "TQ", "TH"  (Review Transactions, Recall Payment for Customers, Reject Payment for Mechants, Recall & Reject History, Trace Queue, Trace History)
    pageTitle,
    pageDescription,
    searchValues,
    setSearchValues,
    dates,   // MOVE DEFINITION TO HERE
    loading,
    errorMessage,
    componentAccess,
    //Events:
    handleReset,
    handleSearch,
    // Extra data for dropdowns in some screens
    financialInstitutionList,
    transactionStatusList,
    subscribeEntityList
  } = props;
  if (!pageType || !searchValues|| !setSearchValues || !handleReset || !handleSearch)
    console.log("ERROR:  some important params are empty in ERPSRecallRejectSearch");
  const { t } = useTranslation();

  const [portalType] = useState(localStorage.getItem("type"));
  const user = useSelector((state) => state.loginUserReducer.user);
  const [isBillerRole] = useState(user.role.role_type === RoleType.BILLER_ADMIN.type || user.role.role_type === RoleType.BILLER_USER.type);
  const [isAdminRole] = useState(user.role.role_type === RoleType.ERPADMIN.type || user.role.role_type === RoleType.CEADMIN.type || user.role.role_type === RoleType.BILLADMIN.type );

  const currentFiNumber = useSelector((state) => state.fiReducer?.selectedFi || user?.fiNumber);
  
  const [fieldMap] = useState(() => {
    // Here we defined which fields are displayed for which screen type
    // "O" - field is optional, "R" - field is Required
    let fields = [];
    if (pageType === "RT") {  // Review Transactions
        fields = [["paymentFromDate", "R"],   ["transactionToAmount", "O"],   ["billerCustomerNumber", "O"], ["subscribeEntityId", "O"],
                  ["paymentToDate", "R"],     ["transactionFromAmount", "O"], ["verificationNumber", "O"]];
        if (!isBillerRole)
          fields.push(["bmoBillerId", "O"], ["billerName", "O"], ["fiCustomerName", "O"]);
        if (isAdminRole) { // "PA", role id 5
            fields.push(["archive", "R"], ["archiveMonth", "R"]);
            if(parseInt(currentFiNumber) === FI.DND)
                fields.push(["selectedFiNumber", "R"]);
        }
        if (isShowBankCard(user.role.role_id, currentFiNumber))
          fields.push(["fiCustomerNumber", "O"]);  // bank card number
    } else if (pageType === "RPC" || pageType === "TP") {    // Recall Payment for Customer  OR  Trace Payments  (the two screens are the same)
        fields = [["paymentToDate", "R"], ["transactionToAmount", "R"], ["billerCustomerNumber", "R"], ["verificationNumber", "O"]];
        if (!isBillerRole)
          fields.push(["bmoBillerId", "O"], ["billerName", "O"], ["fiCustomerName", "O"]);
    } else if (pageType === "RPM") {    // Reject Payment for Merchant
        fields = [["paymentFromDate", "R"], ["selectedFiNumber", "R"],     ["billerCustomerNumber", "O"],
                  ["paymentToDate", "R"],   ["transactionToAmount", "O"],  ["verificationNumber", "O"]];
        if (!isBillerRole)
          fields.push(["bmoBillerId", "R"], ["billerName", "R"], ["fiCustomerName", "O"]);
        if (isShowSubscribingEntity(user.role.role_type, currentFiNumber))  // "PA", role id 5
            fields.push(["subscribeEntityId", "R"]);
    } else if (pageType === "RRH") {    // Recall and Reject History
        fields = [["transactionType", "O"],   ["paymentFromDate", "R"], ["selectedFiNumber", "R"],
                  ["transactionStatus", "R"], ["paymentToDate", "R"],   ["billerCustomerNumber", "O"], ["fiTrackingNumber", "O"] ];
        if (!isBillerRole)
          fields.push(["bmoBillerId", "O"], ["billerName", "O"], ["fiCustomerName", "O"]);
    } else if (pageType === "TH") {     // Trace History
        fields = [["paymentFromDate", "R"], ["paymentToDate", "R"], ["billerCustomerNumber", "O"], ["fiCustomerName", "O"]];
        if (!isBillerRole)
            fields.push(["bmoBillerId", "O"], ["billerName", "O"]);
    } else {
      setValidationErrors(["Error: Unrecongnized pageType " + pageType]);
    }

    // add "fiNumber" for Biller role in BillPay portal if it hasn't been added above
    if (!(pageType === "RPC" || pageType === "TP") && portalType === "billpay" && isBillerRole && fields.length > 0 && !fields.find(e => e[0] === "selectedFiNumber"))
      fields.push(["selectedFiNumber", "R"]);

    return new Map(fields);
  });

  const isShowFiTrackingNumber = (currentFiNumber === FI.CIBC) && fieldMap.get("fiTrackingNumber");

  const sortDetails = [
    { value: "BC", name: t("erps.transactionEnquiry.componentToPrint.customerDetails.label.bankCard") },
    { value: "ED", name: t("erps.transactionEnquiry.page.effectivedate") },
  ];

  const [highlightError, setHighlightError] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const errorRef = useRef();
  const [transactionTypeDisplay, setTransactionTypeDisplay] = useState([]);
  const [loadingMsg, setLoadingMsg] = useState("");

  const [paymentDates] = useState({from: searchValues.paymentFromDate, to: searchValues.paymentToDate});
  const [dateLimits, setDateLimits] = useState( {} );

  useEffect(() => {
    setTransactionTypeDisplay( transactionTypes.map(tt => ({value: tt.value, name: t(tt.name, tt.name)}) ) );
    billerDetails.forEach(bd => bd.name = t(bd.labelKey));
    if (fieldMap.get("archive"))
      archivedList.forEach(al => al.name = t(al.labelKey));

    if (searchValues.archive === "y")
      setMonthlyDateLimits(searchValues.archiveMonth);
    else
      makeDateLimits(dates.minStartDateValue, dates.minEndDateValue, dates.maxDateValue);

    return () => errorRef.current = null;  // clean up the ref on exit
  }, [searchValues]);

  useEffect(() => {
    if (errorRef.current)
        errorRef.current.focus();
  }, [validationErrors.length, errorMessage]);

  useEffect(() => {
    if (!loading && loadingMsg) {
      setLoadingMsg(null);
    }
  }, [loading]);

  const handleFieldChange = (fieldName, newValue) => {
    if (loading) {
      setLoadingMsg("The page is loading, please wait");
    } else {
      let validInput = true;
      if (fieldName === "transactionToAmount" || fieldName === "transactionFromAmount") {
        const [wholePart, decimalPart] = String(newValue).split(".") || [String(newValue), null];
        validInput = wholePart.length < 20 && (!decimalPart || decimalPart.length < 3);
      } else if (fieldName === "verificationNumber") {
        if (newValue)
          validInput = Regex.alphaNumericWithHypen.test(newValue);
      } else if (fieldName === "fiTrackingNumber") {
        if (newValue)
          validInput = Regex.alphaNumeric.test(newValue) && newValue.length <= 15;
      } else if (fieldName === "archive") {
        if (newValue === "n") {
          searchValues.paymentFromDate = paymentDates.from;
          searchValues.paymentToDate = paymentDates.to;
          makeDateLimits(dates.minStartDateValue, dates.minEndDateValue, dates.maxDateValue);
        } else {
            // When switching to archive "yes", initialize archiveMonth to a valid default
            if (!searchValues.archiveMonth || new Date(searchValues.archiveMonth) > dates.minStartDateValue) {
                // Set to the minStartDateValue month as default (the maximum allowed month)
                searchValues.archiveMonth = new Date(dates.minStartDateValue.getFullYear(), dates.minStartDateValue.getMonth(), 1);
            }
          paymentDates.from = searchValues.paymentFromDate;
          paymentDates.to = searchValues.paymentToDate;
          setMonthlyDateLimits(searchValues.archiveMonth, true);
        }
      } else if (fieldName === "archiveMonth") {
        setMonthlyDateLimits(newValue, true);
      }
      if (validInput)
        setSearchValues( {...searchValues, [fieldName]: newValue} ); // invoke setSearchValues(..)  to trigger screen rendering with the new value
    }
  };

  const setMonthlyDateLimits = (archiveMonth, isMonthChanged) => {
    let monthStart = new Date(archiveMonth);
    monthStart.setDate(1);
    let monthEnd = adjustDate(monthStart, -1, 1);
    if (monthEnd >= dates.minStartDateValue)
        monthEnd = adjustDate(dates.minStartDateValue, -1);
    makeDateLimits(monthStart, monthStart, monthEnd);
    if (isMonthChanged) {
      searchValues.paymentFromDate = monthStart;
      searchValues.paymentToDate = monthEnd;
    }
  };

  const makeDateLimits = (minStartDate, minEndDate, maxDate) => {
    setDateLimits ({
      minStart: minStartDate,
      minEnd: minEndDate,
      max: maxDate,
      minStartDisplay: !minStartDate ? "" : moment(minStartDate).format("YYYY-MM-DD"),
      minEndDisplay: !minEndDate ? "" : moment(minEndDate).format("YYYY-MM-DD"),
      maxDisplay: !maxDate ? "" : moment(maxDate).format("YYYY-MM-DD")
    });
  };

  const resetClicked = () => {
    setHighlightError({});
    setValidationErrors([]);
    if (handleReset)
        handleReset();
  };

  const searchClicked = () => {
    const groomedValues = { ...sanitizeQueryParams({ ...searchValues }) };
    const { errors, highlightError } = validateSearchValues(groomedValues);
    if (errors.length) {
      setValidationErrors(errors);
      setHighlightError(highlightError);
    } else {
      if (pageType === "RT" && !checkMiminmumThreeFields(groomedValues)) { 
        setValidationErrors([ t("erps.transactionEnquiry.page.errorMessage.minimumthreefields.required") ]);
        
        // Mark all empty fields with error state for multi-field validation
        const emptyFieldsHighlight = {};
        const allOptionalFields = [
          'subscribeEntityId',
          'transactionFromAmount',
          'transactionToAmount',
          'verificationNumber',
          'bmoBillerId',
          'billerName',
          'paymentMethod',
          'transactionType',
          'cardNumber',
          'accountNumber',
          'transitNumber'
        ];
        
        allOptionalFields.forEach(fieldName => {
          if (fieldMap.get(fieldName) && (!groomedValues[fieldName] || groomedValues[fieldName] === '')) {
            emptyFieldsHighlight[fieldName] = true;
          }
        });
        
        setHighlightError(emptyFieldsHighlight);
      } else {
        setValidationErrors([]);
        setHighlightError({});

        groomedValues.daysCount = dateDiffInDays(groomedValues.paymentFromDate, groomedValues.paymentToDate);
        groomedValues.currentFiNumber = currentFiNumber;

        if (!isShowFiNumberDropdown())  // When FI dropdown is hidden,  we assume that Current FI number is selected
          groomedValues.selectedFiNumber = currentFiNumber;

        handleSearch(groomedValues);
      }
    }
  };

  function checkMiminmumThreeFields(groomedValues) {
    const {
      transactionType, searchBy, offset, limit, archive, archiveMonth, selectedFiNumber, billerId, bmoBillerId, billerName, sortBy, // Excluding these fields from the mininum field count
      ...rest
    } = groomedValues;
    let enteredValues = 0;
    if (rest && typeof rest === "object") {
      for (var key in rest)
        if (rest[key])
          enteredValues++;
    }

    if (isShowFiNumberDropdown() && selectedFiNumber)
      enteredValues++;
    if (!isBillerRole && (bmoBillerId || billerName))
      enteredValues++;
    return enteredValues >= 3;
  }

  const validateSearchValues = (req) => {
    let result = { errors: [], highlightError: {}};

    if (fieldMap.get("archive") && req.archive === "y") {
      if (!req.archiveMonth)
        isValidValue(req, "archiveMonth", result, "erps.common.label.month");
    }

    const date1 = new Date(req.paymentFromDate);
    const date2 = new Date(req.paymentToDate);

    let pfd = fieldMap.get("paymentFromDate");
    if (pfd && isValidValue(req, "paymentFromDate", result, "erps.common.label.paymentStartDate", (v) => (v != "Invalid date"))) {
        if (dateLimits.minStart && date1.getTime() < dateLimits.minStart.getTime()) 
            markError(result, "paymentFromDate",
                      t("erps.common.errorMessage.notBeforeMinimalDate", {fieldLabel: t("erps.common.label.paymentStartDate"), minimalDate: dateLimits.minStartDisplay}));
        else if (dateLimits.max && date1.getTime() > dateLimits.max.getTime())
            markError(result, "paymentFromDate",
                      t("erps.common.errorMessage.notAfterMaximalDate", {fieldLabel: t("erps.common.label.paymentStartDate"), maximalDate: dateLimits.maxDisplay}));
    }
    let ptd = fieldMap.get("paymentToDate");
    if (fieldMap.get("paymentToDate")) {   // if Payment End Date is displayed
      let paymentToDateLabel = pfd ? "erps.common.label.paymentEndDate" : "erps.common.label.paymentDate";
      if (isValidValue(req, "paymentToDate", result, paymentToDateLabel, (v) => (v != "Invalid date"))) {
        if (dateLimits.minEnd && date2.getTime() < dateLimits.minEnd.getTime())
            markError(result, "paymentToDate",
                      t("erps.common.errorMessage.notBeforeMinimalDate", {fieldLabel: t(paymentToDateLabel), minimalDate: dateLimits.minEndDisplay}));
        else if (dateLimits.max && date2.getTime() > dateLimits.max.getTime())
            markError(result, "paymentToDate",
                      t("erps.common.errorMessage.notAfterMaximalDate", {fieldLabel: t(paymentToDateLabel), maximalDate: dateLimits.maxDisplay}));
      }
      if (pfd && ptd) {
          if (date2.getTime() < date1.getTime())
              markError(result, ["paymentFromDate", "paymentToDate"],
                        t("erps.common.errorMessage.dateCompare", {earlierDate: t("erps.common.label.paymentStartDate"), laterDate: t(paymentToDateLabel)}));
          else if (date2.getTime() - date1.getTime() > dates.maxDateIntervalMs)
              markError(result, ["paymentFromDate", "paymentToDate"], t("erps.transactionEnquiry.page.errorMessage.dateRangeForOneMonth"));
      }
    }

    const amounts = [];
    if (fieldMap.get("transactionFromAmount")) {  // if field transactionFromAmount is displayed
      if (isValidValue(req, "transactionFromAmount", result, "erps.common.label.paymentFromAmount")) {
        if (!Regex.decimal2.test(req.transactionFromAmount)) {
            markError(result, "transactionFromAmount", t("erps.common.errorMessage.maxTwoDecimals", {fieldLabel: t("erps.common.label.paymentFromAmount")}));
        } else {
            amounts[0] = parseFloat(req.transactionFromAmount);
            if (amounts[0] < 0)
                markError(result, "transactionFromAmount", t("erps.common.errorMessage.greaterOrEqualZero", {fieldLabel: t("erps.common.label.paymentFromAmount")}));
        }
      }
    }
    if (fieldMap.get("transactionToAmount")) {  // if field transactionToAmount is displayed
      let label = fieldMap.get("transactionFromAmount") ? "erps.common.label.paymentToAmount" : "erps.common.label.paymentAmount";
      if (isValidValue(req, "transactionToAmount", result, label)) {
        if (!Regex.decimal2.test(req.transactionToAmount)) {
            markError(result, "transactionToAmount", t("erps.common.errorMessage.maxTwoDecimals", {fieldLabel: t(label)}));
        } else {
            amounts[1] = parseFloat(req.transactionToAmount);
            if (amounts[1] < 0)
                markError(result, "transactionToAmount", t("erps.common.errorMessage.greaterOrEqualZero"), {fieldLabel: t(label)});
        }
      }
      if (amounts[0] >= 0 && amounts[1] >= 0 && amounts[0] > amounts[1])
        markError(result, ["transactionFromAmount", "transactionToAmount"], t("erps.transactionEnquiry.page.errorMessage.paymentFromLessThanPaymentTo"));
    }

    if (fieldMap.get("bmoBillerId") || fieldMap.get("billerName")) {
      if (req.searchBy === billerDetails[0].value) {  // "billerId"
          isValidValue(req, "bmoBillerId", result, "erps.common.label.billerID", (v) => Regex.alphaNumeric.test(v));
      } else {  // i.e. req.searchBy === "billerName"
          if (isValidValue(req, "billerName", result, null, (v) => Regex.name.test(v))) {
            if ((req.billerName.includes("%") && req.billerName.length < 4) || !Regex.nameWildChar.test(req.billerName) ||
                    (req.billerName.includes("%") && !maxCharOccurrence(req.billerName, "%", 2)) ) {
                markError(result, "billerName", t("erps.transactionEnquiry.page.errorMessage.billerName.minChar"));
            }
          }
      }
    }

      if (fieldMap.get("subscribeEntityId")) {
          isValidValue(req, "subscribeEntityId", result, "erps.common.label.subscribing.entity");
      }


// UNCOMMENT LINE BELOW - DO NOT CHECK IN UNTIL UNCOMMENTED !!!  
    isValidValue(req, "verificationNumber", result, "erps.common.label.confirmationNumber", (v) => Regex.alphaNumericWithHypen.test(v));
    isValidValue(req, "billerCustomerNumber", result, "erps.common.label.customerAccountNumber", (v) => Regex.alphaNumeric.test(v));
    isValidValue(req, "fiCustomerName", result, "erps.common.label.customerName", (v) => Regex.alphaNumericWithMarks.test(v));

    return { ...result };
  };

  function isValidValue(req, fieldName, result, fieldUILabel, validityCheck) {
    const fieldLabel = fieldUILabel || "erps.common.label." + fieldName;
    let errorMsg = checkFieldValue(req[fieldName], fieldLabel, "R" === fieldMap.get(fieldName), validityCheck);
    if (errorMsg === "")
      return !!req[fieldName];  // true if a valid non-empty value is entered
    markError(result, fieldName, errorMsg);
    return false;
  }

  function markError(result, fieldName, errorText) {
    if (errorText)
        result.errors.push(errorText);
    if (fieldName) {
        if (Array.isArray(fieldName))
          fieldName.forEach(attr => result.highlightError[attr] = true);
        else
          result.highlightError[fieldName] = true;
    }
  }

  function isShowFiNumberDropdown() {
    if (!fieldMap.get("selectedFiNumber"))   // hide FI Number dropdown when it's not configured for display
      return false;
    if (isBillerRole || isAdminRole)
      return true;
    // for FI role - we hide fiNumber in RRH screen when transactionType is "RT" (recall)
    return  pageType === "RPM" || (pageType === "RRH" && searchValues?.transactionType === "RJ");
  }

  return (
    <ResponsiveBox sx={{ ml: 0 }}>
        <ErrorMessage 
          error={validationErrors?.length ? validationErrors : errorMessage}
          errorRef={errorRef}
          mt={2}
        />

        <ResponsiveBox className="manageUsers__page__title">
          {/* For Title update */}
          <CommonContainerBox title={pageTitle} />
          <Typography variant="h1">{pageTitle}</Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 4 }}>
            <Typography variant="h2">{pageDescription}</Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 0 }}>
            <Typography variant="body1">{t("erps.transactionEnquiry.page.wildcardDescription")}</Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 0 }}>
            <Typography variant="body1">
                <Box component="span" className="MuiFormLabel-asterisk">*</Box>
                {" "}{t("erps.common.label.required.information")}
            </Typography>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 3 }}>
            <Typography variant="h3">{t("erps.transactionEnquiry.page.transactionDetails.page.subheading")}</Typography>
        </ResponsiveBox>

        {fieldMap.get("archive") && (
          <HStack spacing={2}>
            <ResponsiveBox sx={{ pt: 1, pb: 2 }}>
              <ERPSBaseRadioGroup
                name="archive"
                marginTop={true}
                InputLabelProps={{ shrink: true }}
                value={searchValues?.archive}
                label={"Archived"}
                onChange={(e) => handleFieldChange("archive", e.target.value)}
                items={archivedList}
                error={highlightError?.archive}
                labelPlacement="start"
                className={"transactionEnquiry__radioButton_direction"}
                // actionType={"hasBillerIdTFAccess"}
                // componentAccess={componentAccess}
              />
            </ResponsiveBox>
            {searchValues.archive == "y" && (
              <ResponsiveBox>
                <ERPSBaseDatePicker
                  label={t("erps.common.label.month")}
                  value={searchValues.archiveMonth}
                  required={true}
                  onChange={(e) => handleFieldChange("archiveMonth", e)}
                  format={"YYYY-MM"}
                  views={["year", "month"]}
                  // error={getErrorFields().includes("periodValue")}
                  minDate={dates?.minArchiveMonth}
                  maxDate={dates?.minStartDateValue}
                  animateYearScrolling={false}
                  hasMargin="true"
                  //className="statistics__datepicker"
                  //className="app_datepicker_biller_transactionEnquiry"
                  datePickerStyle={"quick-reg-datepicker-v1"}
                  //size="small"
                />
              </ResponsiveBox>
            )}
          </HStack>
        )}

        {fieldMap.get("transactionType") && (
            <HStack sx={{ mt: -1 }}>
                <ResponsiveBox>
                <ERPSBaseRadioGroup id="transactionType"
                    value={searchValues?.transactionType}
                    name="transactionType"
                    //InputLabelProps={{ shrink: true }}
                    onChange={e => handleFieldChange("transactionType", e.target.value)}
                    items={transactionTypeDisplay}
                    label=""   // seems like it's a mandatory prop
                    //actionType={"hasURRSearchAccess"}   // DO I DEFINE A PERMISSION FOR THIS RADIO BTN ??
                    //className={"transactionEnquiry__radioButton_direction"}  // this removes the top margin
                    marginTop="true"   // this eliminates the default top margin which is quite large
                />
                </ResponsiveBox>
            </HStack>)
        }

        {(pageType !== "RPC" && pageType !== "TP") && (
            <HStack sx={{ mt: -2 }} spacing={2}>
                {fieldMap.get("paymentFromDate") && (
                  <ERPSBaseDatePicker id="paymentFromDate"
                    value={searchValues?.paymentFromDate}
                    label={t("erps.common.label.paymentStartDate")}
                    onChange={e => handleFieldChange("paymentFromDate", e)}
                    className="app_datepicker_biller_transactionEnquiry"
                    hasMargin="true"
                    required={"R" === fieldMap.get("paymentFromDate")}  // must be boolean for ERPSBaseDatePicker
                    datePickerStyle={"quick-reg-datepicker-v1"}
                    minDate={dateLimits.minStart}
                    maxDate={dateLimits.max}
                    error={highlightError?.paymentFromDate} />
                )}
                {fieldMap.get("paymentToDate") && (
                  <ERPSBaseDatePicker id="paymentToDate"
                    value={searchValues?.paymentToDate}
                    label={ t(fieldMap.get("paymentFromDate") ? "erps.common.label.paymentEndDate" : "erps.common.label.paymentDate") }
                    onChange={e => handleFieldChange("paymentToDate", e)}
                    className="app_datepicker_biller_transactionEnquiry"
                    hasMargin="true"
                    required={"R" === fieldMap.get("paymentToDate")}  // must be boolean for ERPSBaseDatePicker
                    datePickerStyle={"quick-reg-datepicker-v1"}
                    minDate={dateLimits.minEnd}
                    maxDate={dateLimits.max}
                    error={highlightError?.paymentToDate} />
                )}
                {fieldMap.get("transactionStatus") && (
                <ERPSBaseTextField id="transactionStatus"
                  value={searchValues?.transactionStatus}
                  type="text"
                  select={true}
                  label={t("erps.common.label.status", "Status")}
                  className="two_column__textField"
                  onChange={e => handleFieldChange("transactionStatus", e.target.value)}
                  size="small"
                  required={String("R" === fieldMap.get("transactionStatus"))}  // there's never an empty value in the dropdown anyway
                  items={transactionStatusList}
                  SelectProps={{ native: true }} />
                )}

                {isShowFiNumberDropdown() ? (
                  <ERPSBaseTextField id="selectedFiNumber"
                    value={searchValues?.selectedFiNumber}
                    type="text"
                    select={true}
                    label={t("erps.common.label.financialInstitution", "Financial Institution")}
                    className="three_column__textField"
                    onChange={e => handleFieldChange("selectedFiNumber", e.target.value)}
                    size="small"
                    required={String("R" === fieldMap.get("selectedFiNumber"))}  // there's never an empty value in the dropdown anyway
                    items={financialInstitutionList}
                    SelectProps={{ native: true }} />
                ) : isShowFiTrackingNumber ? (
                    <ERPSBaseTextField id="fiTrackingNumber"
                      value={searchValues?.fiTrackingNumber}
                      label={t("erps.transactionEnquiry.label.trackingNumber")}
                      className="transactionEnquiry__textField__width"
                      size="small"
                      required={String("R" === fieldMap.get("fiTrackingNumber"))}
                      onChange={(e) => handleFieldChange("fiTrackingNumber", e.target.value) }
                      error={highlightError?.fiTrackingNumber} />
                ) : null}
                {fieldMap.get("subscribeEntityId") && subscribeEntityList && (
                  <ERPSBaseTextField
                    id="subscribeEntityId"
                    type="text"
                    select={true}
                    label={t("erps.newuser.label.subscribingentityid", "Subscribing entity id")}
                    className="transactionEnquiry__textField__width"
                    size="small"
                    items={subscribeEntityList}
                    onChange={(e) => handleFieldChange("subscribeEntityId", e.target.value)}
                    value={searchValues?.subscribeEntityId}
                    required={String("R" === fieldMap.get("subscribeEntityId"))}
                    SelectProps={{ native: true }}
                    error={highlightError?.subscribeEntityId}
                    actionType={"hasSubscribingEntityIdTFAccess"}
                    componentAccess={componentAccess}
                  />
                )}
            </HStack>
        )}

        {/* For RRH screen only: field "fiTrackingNumber" is shown in this <HStack> if FI dropdown is visible */}
        {pageType === "RRH" && isShowFiTrackingNumber && isShowFiNumberDropdown() && (
          <HStack sx={{ mt: -2 }} spacing={2}>
            <ERPSBaseTextField id="fiTrackingNumber"
              value={searchValues?.fiTrackingNumber}
              label={t("erps.transactionEnquiry.label.trackingNumber")}
              className="transactionEnquiry__textField__width"
              size="small"
              required={String("R" === fieldMap.get("fiTrackingNumber"))}
              onChange={(e) => handleFieldChange("fiTrackingNumber", e.target.value) }
              error={highlightError?.fiTrackingNumber} />
          </HStack>
        )}

        {(fieldMap.get("transactionFromAmount") || fieldMap.get("transactionToAmount") || fieldMap.get("verificationNumber")
          || ((pageType === "RPC" || pageType === "TP") && fieldMap.get("paymentToDate")) ) && (
            <HStack sx={{ mt: -2 }} spacing={2}>
                {/* For RPC and TP screens only: field "paymentToDate" is shown in this <HStack>, i.e. in the same row as Amount and Verification Number.
                    For all other screen types,  field "paymentToDate" is shown in the <HStack> above. */}
                {(pageType === "RPC" || pageType === "TP") && fieldMap.get("paymentToDate") && (
                  <ERPSBaseDatePicker id="paymentToDate"
                    value={searchValues?.paymentToDate}
                    label={t("erps.common.label.paymentDate", "Payment date")}
                    onChange={e => handleFieldChange("paymentToDate", e)}
                    className="app_datepicker_biller_transactionEnquiry"
                    hasMargin="true"
                    required={"R" === fieldMap.get("paymentToDate")}  // must be boolean for ERPSBaseDatePicker
                    datePickerStyle={"quick-reg-datepicker-v1"}
                    minDate={dates?.minEndDateValue}
                    maxDate={dates?.maxDateValue}
                    error={highlightError?.paymentToDate} />
                )}

                {fieldMap.get("transactionFromAmount") && (
                  <ERPSBaseTextField id="transactionFromAmount"
                    value={searchValues?.transactionFromAmount}
                    type="number"
                    allowDecimal={true}
                    label={t("erps.common.label.paymentFromAmount")}
                    className="transactionEnquiry__textField__width"
                    size="small"
                    required={String("R" === fieldMap.get("transactionFromAmount"))}   // must be string "true"
                    onChange={(e) => handleFieldChange("transactionFromAmount", e.target.value) }
                    error={highlightError?.transactionFromAmount} />
                )}
                {fieldMap.get("transactionToAmount") && (
                  <ERPSBaseTextField id="transactionToAmount"
                    value={searchValues?.transactionToAmount}
                    type="number"
                    allowDecimal={true}
                    label={ t(fieldMap.get("transactionFromAmount") ? "erps.common.label.paymentToAmount" : "erps.common.label.paymentAmount") }
                    className="transactionEnquiry__textField__width"
                    size="small"
                    required={String("R" === fieldMap.get("transactionToAmount"))}   // must be string "true"
                    onChange={(e) => handleFieldChange("transactionToAmount", e.target.value) }
                    error={highlightError?.transactionToAmount} />
                )}
                {/*Confirmation number input*/}
                {fieldMap.get("verificationNumber") && (
                  <ERPSBaseTextField id="verificationNumber"
                    value={searchValues?.verificationNumber}
                    allowDecimal={false}
                    label={t("erps.common.label.confirmationNumber")}
                    className="transactionEnquiry__textField__width"
                    size="small"
                    required={String("R" === fieldMap.get("verificationNumber"))}
                    onChange={(e) => handleFieldChange("verificationNumber", e.target.value) }
                    error={highlightError?.verificationNumber} />
                )}
            </HStack>
        )}

        {(fieldMap.get("bmoBillerId") || fieldMap.get("billerName")) ? (
          <>
            <ResponsiveBox sx={{ mt: 3 }}>
              <Typography variant="h3">
                {t("erps.common.label.billerDetails")}
              </Typography>
            </ResponsiveBox>
            <HStack sx={{ mt: 0 }} spacing={2}>
                <ResponsiveBox sx={{ mt: 0 }}>
                  <ERPSBaseRadioGroup id="searchBy"
                      value={searchValues?.searchBy}
                      name="searchBy"
                      InputLabelProps={{ shrink: true }}
                      label={t("erps.common.label.searchBy")}
                      onChange={e => handleFieldChange("searchBy", e.target.value)}
                      items={billerDetails}
                      error={highlightError?.searchBy}
                      labelPlacement="start"
                      marginTop={true}  // What is this for??
                      className={"transactionEnquiry__radioButton_direction"} />
                </ResponsiveBox>
                <ResponsiveBox>
                  {searchValues?.searchBy === "billerId" && (
                      <ERPSBaseTextField id="bmoBillerId"
                          value={searchValues?.bmoBillerId}
                          required={String("R" === fieldMap.get("bmoBillerId"))}
                          type="text"
                          label={t("erps.common.label.billerID")}
                          className="transactionEnquiry__textField__width"
                          hasMargin="true"
                          size="small"
                          onChange={(e) => handleFieldChange("bmoBillerId", e.target.value)}
                          error={highlightError?.bmoBillerId}
                          inputProps={{ maxLength: 9 }} />
                  )}
                  {searchValues?.searchBy === "billerName" && (
                      <ERPSBaseTextField id="billerName"
                          value={searchValues?.billerName}
                          required={String("R" === fieldMap.get("billerName"))}
                          type="text"
                          label={t("erps.common.label.billerName")}
                          className="transactionEnquiry__textField__width"
                          hasMargin="true"
                          size="small"
                          onChange={(e) => handleFieldChange("billerName", e.target.value)}
                          error={highlightError?.billerName}
                          inputProps={{ maxLength: 45 }} />
                  )}
                </ResponsiveBox>
            </HStack>
          </>
        ) : null}

        {(fieldMap.get("billerCustomerNumber") || fieldMap.get("fiCustomerName") || fieldMap.get("fiCustomerNumber")) ? (
          <>
            <ResponsiveBox sx={{ mt: 3 }}>
              <Typography variant="h3">
                  {t("erps.common.label.customerDetails")}
              </Typography>
            </ResponsiveBox>
            <HStack sx={{ mt: 2 }} spacing={2}>
              <ERPSBaseTextField id="billerCustomerNumber"
                  value={searchValues?.billerCustomerNumber}
                  required={String("R" === fieldMap.get("billerCustomerNumber"))}
                  type="text"
                  label={t("erps.common.label.customerAccountNumber")}
                  className="transactionEnquiry__textField__width"
                  hasMargin="true"
                  size="small"
                  onChange={(e) => handleFieldChange("billerCustomerNumber", e.target.value) }
                  error={highlightError?.billerCustomerNumber}
                  inputProps={{ maxLength: 45 }} />

              {(!fieldMap.get("fiCustomerName")) ? null : (
                <ERPSBaseTextField id="fiCustomerName"
                  value={searchValues?.fiCustomerName}
                  required={String("R" === fieldMap.get("fiCustomerName"))}
                  type="text"
                  label={t("erps.common.label.customerName")}
                  className="transactionEnquiry__textField__width"
                  hasMargin="true"
                  size="small"
                  onChange={(e) => handleFieldChange("fiCustomerName", e.target.value)}
                  error={highlightError?.fiCustomerName}
                  inputProps={{ maxLength: 30 }} />
              )}
              {(fieldMap.get("fiCustomerNumber")) ? (
                <ERPSBaseTextField id="fiCustomerNumber"
                  value={searchValues?.fiCustomerNumber}
                  required={String("R" === fieldMap.get("fiCustomerNumber"))}
                  type="text"
                  label={t("erps.transactionEnquiry.page.label.customerBankCardName")}
                  className="transactionEnquiry__textField__width"
                  hasMargin="true"
                  size="small"
                  onChange={(e) => handleFieldChange("fiCustomerNumber", e.target.value)}
                  error={highlightError?.fiCustomerNumber}
                  actionType={"hasCustomerBankCardNumTFAccess"}
                  componentAccess={componentAccess}
                  inputProps={{ maxLength: 30 }} />
              ) : null}
            </HStack>
          </>
        ) : null}

          <HStack spacing={2} mt={1}>
            <ResponsiveBox mt={2}>
                <ERPSBaseRadioGroup
                  marginTop={"true"}
                  name="sortBy"
                  InputLabelProps={{ shrink: true }}
                  value={searchValues?.sortBy || "BC"}
                  label={t("erps.transactionEnquiry.page.label.sortBy")}
                  onChange={(e) => handleFieldChange("sortBy", e.target.value)}
                  items={sortDetails}
                  labelPlacement="start"
                  className={"transactionEnquiry__radioButton_direction"}
                  actionType={"hasSortOrderAccess"}
                  componentAccess={componentAccess}
                />
            </ResponsiveBox>
          </HStack>

        <ResponsiveBox mt={4}>
          <HStack spacing={2}>
            <ERPSBaseButton
                onClick={resetClicked}
                variant="secondary"
                className="button_with_margin48"
                label={t("erps.common.button.reset")}
                //componentAccess={componentAccess}
                actionType={"hasTransactionResetBtnAccess"}
            />
            <ERPSBaseButton
                onClick={searchClicked}
                variant="primary"
                className="button_with_margin48"
                label={t("erps.common.button.search")}
                loading={loading}
            />
          </HStack>
        </ResponsiveBox>
        <ResponsiveBox sx={{ mt: 3 }}>
            <Typography variant="h2"> {t("erps.manageusers.searchuser.searchresults")} </Typography>
        </ResponsiveBox>
    </ResponsiveBox>
  );
};

export default ERPSRecallRejectSearch;

const adjustDate = (date, addDays, addMonths, addYears) => {
    let result = date ? new Date(date) : new Date();
    if (addYears)
      result.setFullYear(date.getFullYear() + addYears);
    if (addMonths)
      result.setMonth(date.getMonth() + addMonths);
    if (addDays)
      result.setDate(date.getDate() + addDays);
    return result;
};

const dateDiffInDays = (a, b) => {
  let date1 = new Date(moment(a));
  let date2 = new Date(moment(b));
  
  // UTC dates are used to avoid a 'short' day when switching to summer time
  const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate());
  const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate());
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
};

export const getDateLimits = (pageType, isERPSAdminRole) => {
    let now = new Date();
    now = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let daysToSubtractForStart = -90; // Default
    let daysToSubtractForEnd = -89; // Default

    if (isERPSAdminRole && pageType === "RT") {
        const baseDays = 120;
        const adjustedDays = getDaysToSubtractForSunday(now, baseDays);
        daysToSubtractForStart = -adjustedDays;
        daysToSubtractForEnd = -(adjustedDays - 1);
    }


    let minStartDateValue = adjustDate(now, minStartDateAdjustments[pageType] || daysToSubtractForStart); // Default to '-90' if not found
    let minEndDateValue = adjustDate(now, minEndDateAdjustments[pageType] || daysToSubtractForEnd); // Default to '-89' if not found
    return {
      today: now,
      aMonthAgo: adjustDate(now, -31),
      archiveMonth: adjustDate(now, -121),
      minStartDateValue: minStartDateValue,
      minEndDateValue: minEndDateValue,
      maxDateValue: now,
      maxDateIntervalMs: 31*24*60*60*1000 + 60*60*1000,  // 31 days + 1hr in milliseconds (+1hr to account for an extra hour summer time switch)
      // minStartDateDisplay: moment(minStartDateValue).format("YYYY-MM-DD"),
      // minEndDateDisplay: moment(minEndDateValue).format("YYYY-MM-DD"),
      // maxDateDisplay: moment(now).format("YYYY-MM-DD"),
      minArchiveMonth: adjustDate(now, 0, 0, -7)
    };
};


const getDaysToSubtractForSunday = (now ,baseDays) => {

    // Calculate the date after subtracting base days (120)
    const targetDate = adjustDate(now, -baseDays);
    const dayOfWeek = targetDate.getDay();

    // If it's already Sunday (0), return the base days as is
    if (dayOfWeek === 0) {
        return baseDays;
    }

    // For any other day, go back to the previous Sunday
    // Monday (1) -> go back 1 day to Sunday = 121
    // Tuesday (2) -> go back 2 days to Sunday = 122
    // Wednesday (3) -> go back 3 days to Sunday = 123
    // ...
    // Saturday (6) -> go back 6 days to Sunday = 126
    return baseDays + dayOfWeek;
};

export const billerDetails = [
    { value: "billerId",   labelKey: "erps.common.label.billerID" },
    { value: "billerName", labelKey: "erps.common.label.billerName" }
];

export const archivedList = [
  { value: "y", labelKey: "erps.common.label.yes" },
  { value: "n", labelKey: "erps.common.label.no" }
];
